import type { Identifier } from "ra-core";

import type { Option } from "../gulaConstants";

/** VAT treatment of a purchase invoice (see the gula_accounting migration). */
export type VatMode = "normal" | "reverse_charge" | "exempt";

export type IncomeMethod =
  | "card_terminal"
  | "financing"
  | "transfer"
  | "cash"
  | "other";

export type AccSupplier = {
  id: Identifier;
  name: string;
  tax_id?: string | null;
  country: "ES" | "EU" | "other";
  default_category?: string | null;
  default_vat_mode: VatMode;
  default_vat_rate: number;
  default_irpf_rate: number;
  recurring: boolean;
  notes?: string | null;
};

export type AccExpense = {
  id: Identifier;
  supplier_id?: Identifier | null;
  supplier_name: string;
  supplier_tax_id?: string | null;
  invoice_number?: string | null;
  /** YYYY-MM-DD */
  invoice_date: string;
  category: string;
  description?: string | null;
  vat_mode: VatMode;
  base_amount: number;
  vat_rate: number;
  vat_amount: number;
  other_amount: number;
  irpf_rate: number;
  irpf_amount: number;
  /** Generated in the database: what is paid to the supplier */
  total: number;
  deductible_pct: number;
  paid_at?: string | null;
  payment_method?: string | null;
  file_path?: string | null;
  file_name?: string | null;
  notes?: string | null;
  /** review: read by the AI or created automatically, someone has to check it */
  status?: "ok" | "review";
  source?: "manual" | "ai" | "meta_ads";
  external_key?: string | null;
  /** More files of the same expense (e.g. every Meta receipt of the month) */
  extra_files?: AccFile[] | null;
  ai_data?: {
    confidence?: "high" | "medium" | "low";
    warnings?: string[];
    model?: string;
  } | null;
  synced_at?: string | null;
  reviewed_at?: string | null;
  reviewed_by?: Identifier | null;
  created_at?: string;
  updated_at?: string;
};

export type AccFile = { path: string; name: string };

export type AccSettings = {
  id: 1;
  gestor_name?: string | null;
  gestor_email?: string | null;
  gestor_cc?: string | null;
  meta_ad_account_ids: string[];
  meta_last_sync_at?: string | null;
  meta_last_error?: string | null;
};

export type AccSubmission = {
  id: Identifier;
  period_year: number;
  period_quarter: number | null;
  sent_at: string;
  sent_by?: Identifier | null;
  to_email: string;
  expense_count: number;
  income_count: number;
};

/** Every file of an expense: the main one and the extra ones. */
export const filesOf = (expense: AccExpense): AccFile[] => [
  ...(expense.file_path
    ? [{ path: expense.file_path, name: expense.file_name ?? "Factura" }]
    : []),
  ...(expense.extra_files ?? []),
];

export type AccIncome = {
  id: Identifier;
  /** YYYY-MM-DD */
  income_date: string;
  contact_id?: Identifier | null;
  customer_name?: string | null;
  customer_tax_id?: string | null;
  concept: string;
  method: IncomeMethod;
  base_amount: number;
  vat_rate: number;
  vat_amount: number;
  total: number;
  file_path?: string | null;
  file_name?: string | null;
  notes?: string | null;
};

export const EXPENSE_CATEGORIES: Option[] = [
  { value: "equipment", label: "Equipos y recambios (BLUA, filtros…)" },
  { value: "materials", label: "Material de instalación (grifos…)" },
  { value: "installers", label: "Instaladores y técnicos" },
  { value: "shipping", label: "Envíos y transporte" },
  { value: "advertising", label: "Publicidad (Meta, Google…)" },
  { value: "software", label: "Software y suscripciones" },
  { value: "payment_fees", label: "Comisiones de cobro (Stripe, financieras)" },
  { value: "professional", label: "Gestoría y profesionales" },
  { value: "payroll", label: "Sueldos y Seguridad Social" },
  { value: "utilities", label: "Teléfono, luz e internet" },
  { value: "rent", label: "Alquiler" },
  { value: "vehicle", label: "Vehículo y combustible" },
  { value: "other", label: "Otros gastos" },
];

export const VAT_MODES: (Option & { value: VatMode; hint: string })[] = [
  {
    value: "normal",
    label: "Con IVA",
    hint: "Factura española con IVA: ese IVA se recupera.",
  },
  {
    value: "reverse_charge",
    label: "Inversión del sujeto pasivo",
    hint: "Proveedor de la UE que factura sin IVA (Meta, Google…): el IVA se declara y se deduce a la vez; no se paga.",
  },
  {
    value: "exempt",
    label: "Sin IVA",
    hint: "Exento, nómina, tasas o ticket sin IVA desglosado.",
  },
];

export const VAT_RATES = [21, 10, 4, 0];
export const IRPF_RATES = [0, 7, 15, 19];

export const INCOME_METHODS: (Option & { value: IncomeMethod })[] = [
  { value: "card_terminal", label: "TPV" },
  { value: "financing", label: "Financiera" },
  { value: "transfer", label: "Transferencia" },
  { value: "cash", label: "Efectivo" },
  { value: "other", label: "Otro" },
];

export const PAYMENT_METHODS: Option[] = [
  { value: "card", label: "Tarjeta" },
  { value: "transfer", label: "Transferencia" },
  { value: "direct_debit", label: "Domiciliación" },
  { value: "cash", label: "Efectivo" },
];

export const labelFor = (options: Option[], value?: string | null) =>
  options.find((option) => option.value === value)?.label ?? value ?? "";

export const round2 = (value: number) =>
  Math.round((value + Number.EPSILON) * 100) / 100;

/** Parses "1.234,56", "1234.56" or "1234,5" typed by a person. */
export const parseAmount = (text: string): number => {
  const cleaned = text.replace(/[€\s]/g, "");
  if (!cleaned) return 0;
  const normalized =
    cleaned.includes(",") && cleaned.includes(".")
      ? cleaned.replace(/\./g, "").replace(",", ".")
      : cleaned.replace(",", ".");
  const value = Number(normalized);
  return Number.isFinite(value) ? value : 0;
};

export type ExpenseAmounts = {
  base_amount: number;
  vat_amount: number;
  irpf_amount: number;
  total: number;
};

type AmountInput = {
  vatMode: VatMode;
  vatRate: number;
  irpfRate: number;
  otherAmount?: number;
};

/** VAT that ends up in the invoice total (reverse charge VAT is not paid). */
const paidVatRate = ({ vatMode, vatRate }: AmountInput) =>
  vatMode === "normal" ? vatRate / 100 : 0;

/** VAT quota of the invoice: also computed for reverse charge (self-assessed). */
const quotaRate = ({ vatMode, vatRate }: AmountInput) =>
  vatMode === "exempt" ? 0 : vatRate / 100;

/** Amounts of a purchase invoice from its taxable base. */
export const expenseFromBase = (
  base: number,
  input: AmountInput,
): ExpenseAmounts => {
  const vat = round2(base * quotaRate(input));
  const irpf = round2((base * input.irpfRate) / 100);
  const paidVat = input.vatMode === "normal" ? vat : 0;
  return {
    base_amount: round2(base),
    vat_amount: vat,
    irpf_amount: irpf,
    total: round2(base + paidVat + (input.otherAmount ?? 0) - irpf),
  };
};

/** Amounts of a purchase invoice from what was paid (the usual number at hand). */
export const expenseFromTotal = (
  total: number,
  input: AmountInput,
): ExpenseAmounts => {
  const factor = 1 + paidVatRate(input) - input.irpfRate / 100;
  const base = factor > 0 ? (total - (input.otherAmount ?? 0)) / factor : 0;
  return expenseFromBase(base, input);
};

/** Base and VAT of an income with VAT included. */
export const splitVatIncluded = (total: number, vatRate: number) => {
  const base = round2(total / (1 + vatRate / 100));
  return { base_amount: base, vat_amount: round2(total - base) };
};

/** Deductible part of an expense (base and VAT), e.g. 50 % of a car. */
export const deductibleOf = (expense: AccExpense) => {
  const share = (expense.deductible_pct ?? 100) / 100;
  return {
    base: round2(
      (Number(expense.base_amount) + Number(expense.other_amount)) * share,
    ),
    vat: round2(Number(expense.vat_amount) * share),
  };
};

/** An expense still missing what the accountant needs. */
export const needsReview = (expense: AccExpense) =>
  expense.status === "review" ||
  !expense.file_path ||
  (expense.vat_mode !== "exempt" && !expense.supplier_tax_id?.trim());
